import React from 'react';
import styled, { keyframes } from 'styled-components';
import { COLORS } from '../Activities/ActivityMenu/styles/constants';

const TypingContainer = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${({ $compact }) => ($compact ? '0.25rem 0.5rem' : '0.75rem 1rem')};
  margin: ${({ $compact }) => ($compact ? '0.25rem 0' : '0.5rem 0')};
  color: ${COLORS.TEXT_SECONDARY};
  font-size: ${({ $compact }) => ($compact ? '0.75rem' : '0.875rem')};
`;

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

const Dot = styled.span<{ delay: number; $compact: boolean }>`
  display: inline-block;
  width: ${({ $compact }) => ($compact ? '6px' : '8px')};
  height: ${({ $compact }) => ($compact ? '6px' : '8px')};
  border-radius: 50%;
  background-color: ${COLORS.TEXT_SECONDARY};
  animation: ${bounce} 1.4s infinite ease-in-out;
  animation-delay: ${({ delay }) => delay}s;
`;

interface TypingIndicatorProps {
  userName?: string;
  elapsedTime?: number; // in seconds
  /** When set, shown instead of the default elapsed-time message (e.g. SignalR status). */
  statusMessage?: string | null;
  /** When false, render animated dots only. */
  showText?: boolean;
  /** When true, render a smaller indicator (padding + dots). */
  compact?: boolean;
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  userName = 'Assistant',
  elapsedTime = 0,
  statusMessage = null,
  showText = true,
  compact = false,
}) => {
  // Progressive messages based on elapsed time (used when no statusMessage)
  const getMessage = (): string => {
    if (elapsedTime < 3) {
      return `${userName} is typing`;
    } else if (elapsedTime < 10) {
      return `${userName} is thinking...`;
    } else if (elapsedTime < 20) {
      return 'Taking longer than expected, bear with me...';
    } else {
      return 'Still processing, this might take a moment...';
    }
  };

  // Never show "Completed"; use a brief friendly message or fall back to elapsed-time message.
  const resolvedStatus =
    statusMessage != null && statusMessage !== '' && statusMessage !== 'Completed'
      ? statusMessage
      : statusMessage === 'Completed'
        ? 'Message loaded'
        : null;
  const displayText = resolvedStatus ?? getMessage();

  return (
    <TypingContainer $compact={compact}>
      {showText && <span>{displayText}</span>}
      <Dot delay={0} $compact={compact} />
      <Dot delay={0.2} $compact={compact} />
      <Dot delay={0.4} $compact={compact} />
    </TypingContainer>
  );
};
